import { Component, createRef, Fragment } from 'react';

import { Subscription } from 'rxjs';
import { withRequestIdleCallback } from '../../../annotations/request-idle-callback.annotation';
import FileUploadModal from '../../../components/ui/file-upload/file-upload.modal';
import { lazyInject } from '../../../context/module';
import { DropboxFile } from '../../../models/dropbox-file';
import { GoogleDriveFile } from '../../../models/google-drive-file';
import { Logger } from '../../../models/logger';
import { GAService } from '../../../services/analytics.service';
import { DropboxService } from '../../../services/dropbox.service';
import { GoogleDriverService } from '../../../services/google-driver.service';
import { MotivesService } from '../../../services/motives.service';
import { ScriptLoadService } from '../../../services/script-load.service';
import { SpinnerService } from '../../../services/spinner.service';
import { UIContext } from '../../../services/ui-context';
import { WindowService } from '../../../services/window.service';
import type { FileSelectEvent, FileUploadProps } from '../../../types/file-upload.types';
import { GAContants } from '../../../types/ga.types';
import { BrowserUtils } from '../../../ui-utils/browser-utils';
import { StringUtils } from '../../../utils/string-utils';
import Tooltip from '../../ui/tooltip/tooltip.component';
import styles from './file-upload.component.module.scss';


const defaultProps: Partial<FileUploadProps> = {
    acceptedFileTypes: '*',
    uploadBtnText: 'Add Files',
    uploadBtnClassName: '',
    uploadBtnStyles: {},
    showGoogleDrive: true,
    showDropbox: true,
    singleFileSelect: false,
    displayBtnsInAColumn: false,
    fileUploadInputContainerCSSSelector: ".inputElementsContainer",
};

interface FileUploadComponentState {
    fileUploadBtnReady: boolean;
}

/** Optional, additive (image editor a11y-11): absent = today's markup on every other page. */
export interface FileUploadSourceMenuProps {
    /** Accessible name of the caret that opens the upload-source menu (default 'Download options dropdown toggle button'). */
    sourceMenuLabel?: string;
    /** true: the caret gets aria-haspopup="menu" and the list menu / menuitem roles. */
    sourceMenuRoles?: boolean;
}

export default class FileUploadComponent extends Component<FileUploadProps & FileUploadSourceMenuProps, FileUploadComponentState> {
    private readonly logger = Logger.getLogger('FileUploadComponent');

    inputFileRef = createRef<HTMLInputElement>();

    compId = StringUtils.uuid();
    hasDestroyed = false;
    fileInputSub: Subscription;
    _fileSelectButtonClicked = false;

    dialogShown = false;
    selectedAtleastOneFile = false;

    // @ViewChild('imgFileInput') imgFileInput: ElementRef;
    // @ViewChild('dialog') dialog: ElementRef;

    get shouldShowGoogleDrive() {
        return this.props && this.props.showGoogleDrive && this.scriptLoadService.googleApiReady;
    }

    get shouldShowDropbox() {
        return this.props && this.props.showDropbox && this.scriptLoadService.dropboxApiReady;
    }

    get fileSelectBtnsStyle() {
        return this.props && this.props.shouldDisplayBtnsAsPrimary ? 'x-btn-primary' : 'x-btn-gray-dark-1';
    }

    @lazyInject(SpinnerService) private spinner: SpinnerService;
    @lazyInject(GoogleDriverService) private driver: GoogleDriverService;
    @lazyInject(DropboxService) private dropbox: DropboxService;
    @lazyInject(ScriptLoadService) private scriptLoadService: ScriptLoadService;
    @lazyInject(MotivesService) public motivesService: MotivesService;
    @lazyInject(WindowService) public windowService: WindowService;
    // @lazyInject(FileInputButtonService) public fileInputButtonService: FileInputButtonService;
    @lazyInject(UIContext) public uiContext: UIContext;
    @lazyInject(GAService) private gaService: GAService;

    readonly state: Readonly<FileUploadComponentState> = { fileUploadBtnReady: true, };
    constructor(props: FileUploadProps) {
        super(props);
        // this.fileInputButtonService.setAttributes({
        //     multiple: !this.props.singleFileSelect,
        //     hidden: true,
        //     accept: this.props.acceptedFileTypes,
        // });
        /***
         * DONOT CANCEL THIS SUBSCRIPTION. IT IS REQUIRED TO HANDLE FILE UPLOAD REQUEST FROM THE
         * FILE UPLOAD BUTTON AFTER COMPONENT DESTROYED
         */
        // const sub = this.fileInputButtonService.onFileSelect.subscribe({
        //     next: (response) => {
        //         if (response.requestId === this.compId) {
        //             this.processFileSelectedEvent(response.fileSelectEvent);
        //         }

        //         if (this.hasDestroyed) {
        //             RxUtils.safeCloseSubscriber(sub);
        //         }
        //     }
        // });

    }

    componentDidMount() {
        // this.fileInputButtonService.canClick.subscribe({
        //     next: () => {
        //         this.setState({
        //             fileUploadBtnReady: true,
        //         });
        //     }
        // });

        this.gaService.send(
            GAContants.HitType.EVENT,
            GAContants.EventCategory.NETWORK,
            GAContants.EventAction['FILE-UPLOAD-READY'],
            GAContants.EventLabel.UPLOAD,
            '',
        );
    }

    componentWillUnmount() {
        this.hasDestroyed = true;

    }

    get propsWithDefaultValues() {
        return Object.assign(defaultProps, this.props);
    }

    async openFileDialog() {

        if (!await this.showProductMotive()) {
            return;
        }
        await this.initializeScriptsAndWebsockets();
        this.driver.showFileSelectDialog((files: GoogleDriveFile[]) => {
            this.props.fileSelected({ files: files });
            this.close();
        });
        this.selectedAtleastOneFile = true;
    }

    async showProductMotive() {
        // if (this.loginService.motiveSettings.show7DayTrialDialog) {
        //     const result = await this.uiContext.showHTMLDialog();
        //     if (result && result.value) {
        //         this.router.navigate(["product"]);
        //         return false;
        //     }
        // }
        return true;
    }

    async initializeScriptsAndWebsockets() {
        await this.spinner.runWithLoader(async () => {
            await this.scriptLoadService.loadAllScriptsIfNotLoaded();
            this._fileSelectButtonClicked = true;
            this.props.fileSelectButtonClicked();
            this.show();
        }, 'Please wait while we initialize file upload service...');
    }

    show() {
        FileUploadModal.show(this.propsWithDefaultValues);
    }

    private async openGoogleDriveDialog() {
        if (BrowserUtils.isSafari() && !BrowserUtils.runIfWindowExists(() => window['gapi'], false)) {
            this.uiContext.showErrorDialog({
                title: 'Unsupported Browser',
                text: 'Google Drive file upload is not supported in Safari. Please use Chrome browser.',
            });
            return;
        }
        this.windowService.fileSelectBtnClicked();
        if (!await this.showProductMotive()) {
            return;
        }
        await Promise.all([this.scriptLoadService.loadGoogleApi(), this.scriptLoadService.loadGoogleIdentityServices()]);
        this.driver.showFileSelectDialog((files: GoogleDriveFile[]) => {
            this.fileSelected({ files: files });
        });
    }

    private async openDropboxFileDialog() {
        if (BrowserUtils.isSafari() && !BrowserUtils.runIfWindowExists(() => window['Dropbox'], false)) {
            this.uiContext.showErrorDialog({
                title: 'Unsupported Browser',
                text: 'Dropbox file upload is not supported in Safari. Please use Chrome browser.',
            });
            return;
        }
        this.windowService.fileSelectBtnClicked();
        if (!await this.showProductMotive()) {
            return;
        }
        await this.scriptLoadService.loadDropbox();
        this.dropbox.showFileSelectDialog((files: DropboxFile[]) => {
            this.fileSelected({ files: files });
        });
    }

    private async processFileSelectedEvent(ev: Event): Promise<void> {
        ev.stopPropagation();
        ev.preventDefault();
        // TODO is this needed here?
        this.scriptLoadService.loadAllScriptsIfNotLoaded().catch((e) => {
            this.logger.error({ message: 'Something went wrong while loading scripts', error: e });
        });

        const files: File[] = ev['target']['files'] || ev['dataTransfer'].files;

        this.gaService.send(
            GAContants.HitType.EVENT,
            GAContants.EventCategory.NETWORK,
            GAContants.EventAction['FILE-UPLOAD-CLICKED'],
            GAContants.EventLabel.UPLOAD,
            '',
        );

        this.fileSelected({
            files: Array.from(files),
        });
        // this.close();
        this.selectedAtleastOneFile = true;
        // this.fileInputButtonService.clearInput();

        this.inputFileRef.current.value = '';
        this.inputFileRef.current.type = 'text';
        this.inputFileRef.current.type = 'file';
    }

    @withRequestIdleCallback
    private fileSelected(ev: FileSelectEvent) {
        this.props.fileSelected(ev);
    }

    close() {
        this.dialogShown = false;
    }

    handleFileSelectBtnClicked() {
        this.windowService.fileSelectBtnClicked();
        // this.fileInputButtonService.click(this.compId);
        this.inputFileRef.current.click();
    }

    private googleDriveButtonHtml() {
        return <Tooltip text="Select Files From Google Drive">
            <button className={`x-btn ${this.fileSelectBtnsStyle} x-btn-svg text-flex-center`} onClick={() => this.openGoogleDriveDialog()}>
                <img className={!this.props.shouldDisplayBtnsAsPrimary ? "color-invert" : ''} src="/icons/svg/google-drive.svg" />
            </button>
        </Tooltip>;
    }

    private dropboxButtonHtml() {
        return <Tooltip text="Select Files From Dropbox">
            <button className={`x-btn ${this.fileSelectBtnsStyle} x-btn-svg text-flex-center`} onClick={() => this.openDropboxFileDialog()}>
                <img className={!this.props.shouldDisplayBtnsAsPrimary ? "color-invert" : ''} src="/icons/svg/dropbox.svg" />
            </button>
        </Tooltip>;
    }

    private largeFileSelectButtonHtml() {
        return <Fragment>
            <Tooltip text="Select Files From Computer">
                <button className={`x-btn ${this.fileSelectBtnsStyle} text-flex-center`} onClick={() => this.handleFileSelectBtnClicked()}>
                    {this.state.fileUploadBtnReady ? this.props.uploadBtnText || 'Select Files' : 'Loading...'}
                </button>
            </Tooltip>
        </Fragment>;
    }

    private fullWidthFileSelectButtonHtml() {
        return <Fragment>
            <Tooltip text="Select Files From Computer">
                <button className={`x-btn ${this.fileSelectBtnsStyle} text-flex-center ${styles['full-width-btn']}`} onClick={() => this.handleFileSelectBtnClicked()}>
                    {this.state.fileUploadBtnReady ? <div>
                        <div><img src="/icons/svg/plus-circle.svg" /></div>
                        <div>Add Files</div>
                    </div> : 'Loading...'}
                </button>
            </Tooltip>
        </Fragment>;
    }

    private smallFileSelectButtonHtml() {
        return <Fragment>
            <Tooltip text="Select Files From Computer">
                <button className={`x-btn ${this.fileSelectBtnsStyle} text-flex-center`} onClick={() => this.handleFileSelectBtnClicked()}>
                    +
                </button>
            </Tooltip>
        </Fragment>;
    }



    render() {

        if (Date()) {
            return <>
                <div className="btn-group w-100" data-jkey="file-upload-container">
                    <input key={'file-upload-component-file-input'}
                        onInput={(e) => this.processFileSelectedEvent(e.nativeEvent)}
                        ref={this.inputFileRef}
                        type="file"
                        multiple={!this.props.singleFileSelect} hidden={true}
                        // accept={this.props.acceptedFileTypes}
                        data-jkey="file-upload-input"
                    />
                    <button type="button" className={`btn btn-md ${this.props.uploadBtnClassName || 'btn-light'} square-edges w-100`} onClick={() => this.handleFileSelectBtnClicked()}>
                        <img width={"14px"} height={"14px"} className={`${styles['btn-prefix-icon']}`} src="/icons/svg/add-files.svg" alt='Add files' /> <span>
                            {this.state.fileUploadBtnReady ? this.props.uploadBtnText || 'Add Files' : 'Loading...'}
                        </span>
                    </button>
                    <button type="button" className={`btn btn-md ${this.props.uploadBtnClassName || 'btn-light'} square-edges dropdown-toggle dropdown-toggle-split`}
                        data-bs-toggle="dropdown"
                        data-bs-display="static"
                        aria-expanded="false"
                        aria-label={this.props.sourceMenuLabel || 'Download options dropdown toggle button'}
                        aria-haspopup={this.props.sourceMenuRoles ? 'menu' : undefined}
                    >
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end dropdown-menu-lg-start" role={this.props.sourceMenuRoles ? 'menu' : undefined}>
                        <li role={this.props.sourceMenuRoles ? 'none' : undefined}>
                            <button className={`dropdown-item ${styles['dropdown-item']}`} type="button" role={this.props.sourceMenuRoles ? 'menuitem' : undefined} onClick={() => this.handleFileSelectBtnClicked()}>
                                <img className={`color-invert`} src="/icons/svg/computer-screen.svg" alt='Add files from computer' /> From my Computer
                            </button>
                        </li>
                        <li role={this.props.sourceMenuRoles ? 'none' : undefined}>
                            <button className={`dropdown-item ${styles['dropdown-item']}`} type="button" role={this.props.sourceMenuRoles ? 'menuitem' : undefined} onClick={() => this.openGoogleDriveDialog()}>
                                <img className={`color-invert`} src="/icons/svg/google-drive.svg" alt='Add files from Google Drive' /> From Google Drive
                            </button>
                        </li>
                        <li role={this.props.sourceMenuRoles ? 'none' : undefined}>
                            <button className={`dropdown-item ${styles['dropdown-item']}`} type="button" role={this.props.sourceMenuRoles ? 'menuitem' : undefined} onClick={() => this.openDropboxFileDialog()}>
                                <img className={`color-invert`} src="/icons/svg/dropbox.svg" alt='Add file from Dropbox' /> From Dropbox
                            </button>
                        </li>
                    </ul>
                </div>
            </>
        }

        if (this.props.displayBtnsInAGridWithLargeBtnOnTop) {
            return <div className={styles['upload-btn-group']}>
                <div className={`${styles['full-width-upload-btns']} ${styles['grid']}`}>
                    {
                        this.fullWidthFileSelectButtonHtml()
                    }
                    {
                        this.googleDriveButtonHtml()
                    }
                    {
                        this.dropboxButtonHtml()
                    }
                </div>
            </div>
        }
        if (this.props.displayBtnsInAColumn) {
            return <div className={styles['upload-btn-group']}>
                <div className={`${styles['other-upload-btns']} ${styles['vertical']}`}>
                    {
                        this.smallFileSelectButtonHtml()
                    }
                    {
                        this.googleDriveButtonHtml()
                    }
                    {
                        this.dropboxButtonHtml()
                    }
                </div>
            </div>
        }
        return <div className={`${styles['upload-btn-group']}`}>
            <div className={styles['file-upload-btn-parent']}>{
                this.largeFileSelectButtonHtml()
            }</div>
            <div className={styles['other-upload-btns']}>
                {
                    this.googleDriveButtonHtml()
                }
                {
                    this.dropboxButtonHtml()
                }
            </div>
        </div>
    }
}